import { useState } from 'react';
import { useGlobalState } from 'src/state/globalstate';
import { captureError, captureEvent } from 'src/common/analytics';
import { presignS3Url } from 'src/common/s3Url';

/**
 * "No document to hand? Download a sample one."
 *
 * The upload panel is a dead end for anyone who arrived without a PDF — most
 * obviously partway through the guided tour, which asks them to upload one of
 * their own. This gives them a real regulated document to try the whole flow with.
 *
 * The object is private, so the link is presigned on click rather than being a
 * plain href: a bare S3 URL for it comes back 403. It is signed on click, not on
 * mount, so no signature is minted for a panel nobody used, and none is left
 * sitting in the DOM long enough to expire.
 */

/** The sample regulated document. Private object — see presign note above. */
const SAMPLE_URL =
  'https://mlrckymera-2026.s3.us-west-2.amazonaws.com/storage/uploads/Revage_Lift_Prof.pdf';
const SAMPLE_FILENAME = 'Revage_Lift_Prof.pdf';

type Props = {
  /** Overrides the default copy where a different tone suits the surface. */
  label?: string;
  className?: string;
  /**
   * Styling for the guided-tour card: left-aligned and boxed, rather than the
   * centred single line that suits a wide panel. The card is only ~348px, so the
   * prompt and the action have to stack instead of sitting side by side.
   */
  inCard?: boolean;
};

export function SampleDocumentLink({ label, className, inCard }: Props) {
  const [BucketType] = useGlobalState('BucketType');
  const [REGION] = useGlobalState('BucketRegion');
  const [accessKeyId] = useGlobalState('accessKeyId');
  const [secretAccessKey] = useGlobalState('secretAccessKey');

  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const download = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const signed = await presignS3Url(
        SAMPLE_URL,
        { region: REGION, accessKeyId, secretAccessKey },
        // downloadAs is what makes the browser SAVE it rather than open it in a
        // tab: the anchor's `download` attribute is ignored cross-origin.
        { fallbackBucket: BucketType, downloadAs: SAMPLE_FILENAME }
      );
      if (!signed) {
        // Almost always the S3 credentials not having loaded yet (they arrive with
        // the bucket config after sign-in). Say so instead of opening a 403.
        setFailed(true);
        captureEvent('sample_document_presign_failed');
        return;
      }
      captureEvent('sample_document_downloaded');
      const a = document.createElement('a');
      a.href = signed;
      a.rel = 'noopener';
      // Harmless cross-origin (S3's Content-Disposition does the real work), and
      // it gives the right filename if the browser ever does honour it.
      a.download = SAMPLE_FILENAME;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      captureError('components/SampleDocument:download', e);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`ksample${inCard ? ' in-card' : ''}${
        className ? ` ${className}` : ''
      }`}
    >
      <style>{`
        /* style.css forces black on every p/h1-h6, so any colour here has to say
           so explicitly. */
        .ksample{ display:flex; align-items:center; justify-content:center; gap:7px;
          flex-wrap:wrap; margin:10px 0 2px; font-family:'Inter',sans-serif;
          font-size:12.5px; }
        /* In the tour card: stacked and left-aligned, on its own tinted shelf so
           it reads as an aside to the step rather than as the step's instruction. */
        .ksample.in-card{ display:block; text-align:left; margin:14px 0 2px;
          padding:10px 12px; border-radius:10px; background:rgba(108,92,231,.07);
          border:1px solid rgba(108,92,231,.18); line-height:1.5; }
        .ksample.in-card .ksample-btn{ margin-top:3px; }
        .ksample-text{ color:#6f6a91 !important; }
        .ksample-btn{ display:inline-flex; align-items:center; gap:6px; padding:0;
          border:none; background:none; cursor:pointer; font:inherit; font-weight:700;
          color:#6c5ce7 !important; text-decoration:underline;
          text-underline-offset:2px; }
        .ksample-btn:hover{ color:#5a48d0 !important; }
        .ksample-btn:disabled{ cursor:default; opacity:.65; text-decoration:none; }
        .ksample-err{ color:#8a1c3d !important; }
        html.dark .ksample-text{ color:rgba(255,255,255,.6) !important; }
        html.dark .ksample-btn{ color:#a99bf5 !important; }
        html.dark .ksample-err{ color:#f3a6bd !important; }
        html.dark .ksample.in-card{ background:rgba(139,123,240,.12);
          border-color:rgba(139,123,240,.28); }
      `}</style>
      <span className="ksample-text">Don't have a document to hand?</span>
      {inCard && <br />}
      <button
        type="button"
        className="ksample-btn"
        onClick={download}
        disabled={busy}
        title="Download a sample PDF you can upload here"
      >
        {busy ? 'Preparing sample…' : label || 'Download a sample document'}
      </button>
      {failed && (
        <span className="ksample-err">
          Couldn't prepare the sample just now — please try again in a moment.
        </span>
      )}
    </div>
  );
}

export default SampleDocumentLink;
